/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable react/prop-types */
import React, { FC, Fragment } from "react"
import { Modifier, ModifierGroup } from "types/models/menu"
import {
  Button,
  Modal,
  Flex,
  Radio,
  Space,
  RadioGroupProps,
  Row,
  Col,
} from "@duxton-web/ui"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { BottomDrawer } from "components/common"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { ConfirmModal } from "components/common/ConfirmModal"
import { availabilityStatus } from "utils/modifier_group_helper"
import useInventoryContext from "hooks/useInventoryContext"

interface SelectionActionProps {
  checkedModifiers: Modifier[]
  checkedModifierGroups: ModifierGroup[]
  showDeleteCheckedModifiersConfirm: boolean
  showChangeModifierAvailabilityConfirm: boolean
  handleCloseCheckbox: () => void
  onDeleteModifierCheckbox: () => void
  onCancelDeleteCheckedModifer: () => void
  onConfirmDeleteChecked: () => void
  onClickChangeModifierAvailability: () => void
  onChangeSelectedModifierAvailability: RadioGroupProps["onChange"]
  onCancelChangeSelectedModifierAvailability: () => void
  onConfirmChangeSelectedModifierAvailability: () => void
  modifierSelectedAvailability: number | undefined
}

const SelectionAction: FC<SelectionActionProps> = ({
  checkedModifiers,
  checkedModifierGroups,
  showDeleteCheckedModifiersConfirm,
  showChangeModifierAvailabilityConfirm,
  handleCloseCheckbox,
  onDeleteModifierCheckbox,
  onCancelDeleteCheckedModifer,
  onConfirmDeleteChecked,
  onClickChangeModifierAvailability,
  onChangeSelectedModifierAvailability,
  onCancelChangeSelectedModifierAvailability,
  onConfirmChangeSelectedModifierAvailability,
  modifierSelectedAvailability,
}) => {
  const { hideDeleteAction } = useInventoryContext()
  const { isEditAllowed, isStatusChangeAllowed } = useSelector(
    (state: AppState) => state.menu,
  )
  const textMap = useTranslationsMap({
    changeAvailability: "menu.buttons.changeAvailability",
    delete: "menu.buttons.delete",
  })
  const { disableAvailability } = useInventoryContext()

  // Check whether every modifier group have item linked
  const noLinkedToItemModifierGroup: boolean[] = []
  checkedModifierGroups?.forEach(modifierGroup => {
    if (modifierGroup?.relatedItemIDs?.length > 0) {
      noLinkedToItemModifierGroup.push(false)
    } else {
      noLinkedToItemModifierGroup.push(true)
    }
  })
  const noLinkedToItemModifierGroupEvery =
    noLinkedToItemModifierGroup?.length > 0 &&
    noLinkedToItemModifierGroup?.every(item => item)

  const enableDelete =
    (noLinkedToItemModifierGroupEvery ||
      (checkedModifiers?.length && checkedModifierGroups?.length === 0)) &&
    isEditAllowed

  return (
    <Fragment>
      <BottomDrawer visible onClose={handleCloseCheckbox}>
        <Flex align="center" gap={16}>
          <div style={{ fontWeight: 500, width: 200 }}>
            {useTranslation("menu.modifierGroup.modifiersSelected").replace(
              /<x>/i,
              `${checkedModifiers.length}`,
            )}
          </div>

          {!disableAvailability && (
            <Button
              size="small"
              onClick={onClickChangeModifierAvailability}
              disabled={!isStatusChangeAllowed || !checkedModifiers.length}
            >
              {textMap.changeAvailability}
            </Button>
          )}

          {/* Delete button is enable when all checkedModifierGrup
                relatedItemIDs is empty or there is checked modifier
                and no modifier group is checked
            */}
          {!hideDeleteAction && (
            <Button
              data-test="delete-btn"
              size="small"
              danger
              onClick={() => onDeleteModifierCheckbox()}
              disabled={!enableDelete}
            >
              {textMap.delete}
            </Button>
          )}
        </Flex>
      </BottomDrawer>
      <ConfirmModal
        title={useTranslation(
          "menu.modifierGroup.confirm.multipleDelete",
        ).replace(/<x>/i, `${checkedModifiers.length}`)}
        visible={showDeleteCheckedModifiersConfirm}
        onCancel={() => onCancelDeleteCheckedModifer()}
        onConfirm={() => onConfirmDeleteChecked()}
        content={useTranslation(
          "menu.modifierGroup.confirm.cannotRetriveDeleted",
        )}
      />
      <Modal
        data-test="change-availability-modal"
        title={useTranslation("menu.modals.changeAvailability.title")}
        open={showChangeModifierAvailabilityConfirm}
        onCancel={() => onCancelChangeSelectedModifierAvailability()}
        width={460}
        zIndex={10000}
        footer={
          <Row justify="center" gutter={16}>
            <Col span={12}>
              <Button
                style={{ width: "100%", justifyContent: "center" }}
                onClick={() => onCancelChangeSelectedModifierAvailability()}
              >
                {useTranslation("menu.buttons.cancel")}
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                style={{ width: "100%", justifyContent: "center" }}
                onClick={() => onConfirmChangeSelectedModifierAvailability()}
              >
                {useTranslation("menu.buttons.done")}
              </Button>
            </Col>
          </Row>
        }
      >
        <Radio.Group
          onChange={onChangeSelectedModifierAvailability}
          value={modifierSelectedAvailability}
          style={{ marginTop: 16, marginBottom: 16 }}
        >
          <Space direction="vertical" size={14}>
            <Radio value={availabilityStatus.available}>
              {useTranslation("menu.item.statuses.available")}
            </Radio>
            <Radio value={availabilityStatus.unAvailableToday}>
              {useTranslation("menu.item.statuses.unavailableUntilTomorrow")}
            </Radio>
            <Radio value={availabilityStatus.unavailableUntilReactivated}>
              {useTranslation("menu.item.statuses.unavailableIndefinitely")}
            </Radio>
          </Space>
        </Radio.Group>
      </Modal>
    </Fragment>
  )
}

export default SelectionAction
